import React, { FC, useMemo } from 'react';
import { Box, Input, InputArea, FormField } from '@wix/design-system';
import { CreateProductStepMedia } from './CreateProductStepMedia';
import { useTypeWriter } from 'hooks/useTypeWriter';
import styles from '../CreateProductStep.scss';
import clsx from 'clsx';
import { useTranslation } from '@wix/wix-i18n-config';
import { useProductStore } from 'stores/product';
import { getCurrency } from 'locale-currency';
import { getBrowserLocale, toCurrencySymbol } from '@wix/gg-common-lib/utils';

export const CreateProductStepForm: FC = () => {
  const { t } = useTranslation();

  const productName = useProductStore((state) => state.productName);
  const productDescription = useProductStore((state) => state.productDescription);
  const productPrice = useProductStore((state) => state.productPrice);
  const setProductName = useProductStore((state) => state.setProductName);
  const setProductDescription = useProductStore((state) => state.setProductDescription);
  const setProductPrice = useProductStore((state) => state.setProductPrice);

  const { isTyping: isTypingName } = useTypeWriter({
    value: useProductStore.getState().generatedProductName || '',
    setValue: (name) => setProductName(name),
    enabled: !productName,
    ms: 100,
  });

  const { isTyping: isTypingDescription } = useTypeWriter({
    value: useProductStore.getState().generatedProductDescription || '',
    setValue: (description) => setProductDescription(description),
    enabled: !productDescription,
  });

  const { isTyping: isTypingPrice } = useTypeWriter({
    value: useProductStore.getState().generatedProductPrice?.toString() || '',
    setValue: (price) => setProductPrice(price),
    enabled: !productPrice,
    ms: 100,
  });

  const currencySymbol = useMemo(() => {
    const locale = getBrowserLocale();
    return toCurrencySymbol(locale, getCurrency(locale) ?? 'USD');
  }, []);

  return (
    <Box direction="vertical" gap="20px">
      <CreateProductStepMedia />
      <Box gap="20px" gridTemplateColumns="3fr 1fr" display="grid">
        <FormField label={t('create-product-step.form.name.label')}>
          <Input
            className={clsx(styles.createProductStepFormInput, isTypingName && styles.disabled)}
            onChange={(e) => setProductName(e.target.value)}
            value={productName}
          />
        </FormField>
        <FormField
          label={t('create-product-step.form.price.label')}
          infoContent={t('create-product-step.form.price.info')}
        >
          <Input
            prefix={<Input.Affix>{currencySymbol}</Input.Affix>}
            className={clsx(styles.createProductStepFormInput, isTypingPrice && styles.disabled)}
            onChange={(e) => setProductPrice(e.target.value)}
            value={productPrice}
            type="number"
          />
        </FormField>
      </Box>
      <FormField label={t('create-product-step.form.description.label')}>
        <InputArea
          className={clsx(styles.createProductStepFormInput, isTypingDescription && styles.disabled)}
          onChange={(e) => setProductDescription(e.target.value)}
          value={productDescription}
          rows={4}
        />
      </FormField>
    </Box>
  );
};
